import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

export interface SectionTitleWithInfoProps {
  /** i18n key for the section title */
  titleKey: string;
  /** i18n key for the description shown in the info tooltip */
  descriptionKey: string;
  /** Optional custom class for the title wrapper */
  className?: string;
}

export function SectionTitleWithInfo({
  titleKey,
  descriptionKey,
  className = '',
}: SectionTitleWithInfoProps) {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div
      className={`flex items-center gap-2 mb-4 ${className}`.trim()}
      data-testid="section-title-with-info"
    >
      <h3 className="text-lg font-semibold">{t(titleKey)}</h3>
      <div className="relative group">
        <button
          type="button"
          aria-label={t('common.infoDescription')}
          className="text-gray-400 hover:text-gray-600 focus:text-gray-600 focus:outline-none rounded-full p-0.5"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
        >
          <InformationCircleIcon className="h-5 w-5" />
        </button>
        {showTooltip && (
          <div
            role="tooltip"
            className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 z-10 min-w-80 max-w-lg px-4 py-3 text-base text-gray-700 bg-gray-100 border border-gray-200 rounded-lg shadow-md whitespace-normal leading-relaxed"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            {t(descriptionKey)}
            <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-200" />
          </div>
        )}
      </div>
    </div>
  );
}
